import { Routes } from '@angular/router';
import { HomeComponent } from './pages/home/home.component';
import { LoginComponent } from './pages/login/login.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { NotFoundComponent } from '@crowdctrl/shared';
import { authGuard } from './guards/auth.guard';
import { formLockGuard } from './guards/form-lock.guard';
import { downloadPageGuard } from './guards/download-page.guard';
import { publicFormShortUrlGuard } from './guards/public-form-short-url.guard';
import { publicFormContextResolver } from './guards/public-form-context.resolver';
import { publicOrderFollowGuard } from './guards/public-order-follow.guard';
import { publicOrderFollowResolver } from './guards/public-order-follow.resolver';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: 'about/technical',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutTechnicalComponent),
  },
  {
    path: 'about/features',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutFeaturesComponent),
  },
  {
    path: 'about/how-it-works/manager',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksManagerComponent),
  },
  {
    path: 'about/how-it-works/service',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksServiceComponent),
  },
  {
    path: 'about/how-it-works/forms',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksFormsComponent),
  },
  {
    path: 'about/how-it-works/boards',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksBoardsComponent),
  },
  {
    path: 'about/how-it-works',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksComponent),
  },
  {
    path: 'about/future-directions',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutFutureDirectionsComponent),
  },
  {
    path: 'register-interest',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.RegisterInterestShellComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('@crowdctrl/shared').then((m) => m.RegisterInterestComponent),
      },
      {
        path: 'thanks',
        loadComponent: () =>
          import('@crowdctrl/shared').then((m) => m.RegisterInterestThanksComponent),
      },
    ],
  },
  {
    path: 'contact',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.ContactShellComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('@crowdctrl/shared').then((m) => m.ContactComponent),
      },
      {
        path: 'sent',
        loadComponent: () => import('@crowdctrl/shared').then((m) => m.ContactSentComponent),
      },
    ],
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.PrivacyPolicyComponent),
  },
  {
    path: 'terms',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.TermsComponent),
  },
  {
    path: 'about',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutProjectComponent),
  },
  {
    path: 'form-unavailable',
    loadComponent: () =>
      import('./pages/public-short-url/form-unavailable.component').then(
        (m) => m.FormUnavailableComponent,
      ),
  },
  {
    path: 'o/:followId',
    loadComponent: () =>
      import('./pages/public-order-follow/public-order-follow-page.component').then(
        (m) => m.PublicOrderFollowPageComponent,
      ),
    canActivate: [publicOrderFollowGuard],
    resolve: { followData: publicOrderFollowResolver },
  },
  {
    path: 's/:shortId',
    loadComponent: () =>
      import('./pages/public-short-url/public-short-url-form-page.component').then(
        (m) => m.PublicShortUrlFormPageComponent,
      ),
    canActivate: [publicFormShortUrlGuard],
    resolve: { publicFormContext: publicFormContextResolver },
  },
  {
    path: 'forms/:workspaceId/:sessionId/:formId',
    loadComponent: () =>
      import('./pages/forms/form-page/form-page.component').then((m) => m.FormPageComponent),
    canActivate: [authGuard],
  },
  {
    path: 'forms/:workspaceId/:sessionId/:formId/order-placed',
    loadComponent: () =>
      import('./pages/forms/order-placed/order-placed.component').then(
        (m) => m.OrderPlacedPageComponent,
      ),
    canActivate: [authGuard],
  },
  {
    path: '',
    component: HomeComponent,
    canActivate: [authGuard, formLockGuard],
  },
  {
    path: 'settings',
    loadComponent: () =>
      import('./pages/settings/settings.component').then((m) => m.SettingsComponent),
    canActivate: [authGuard, formLockGuard],
  },
  {
    path: 'settings/developer',
    loadComponent: () =>
      import('./pages/settings/developer-settings/developer-settings.component').then(
        (m) => m.DeveloperSettingsComponent,
      ),
    canActivate: [authGuard, formLockGuard],
  },
  {
    path: 'download',
    loadComponent: () =>
      import('./pages/download/download.component').then((m) => m.DownloadComponent),
    canActivate: [downloadPageGuard, authGuard, formLockGuard],
  },
  {
    path: 'workspaces/select',
    loadComponent: () =>
      import('./pages/workspaces/select-workspace/select-workspace.component').then(
        (m) => m.SelectWorkspaceComponent,
      ),
    canActivate: [authGuard, formLockGuard],
  },
  {
    path: 'workspaces/:workspaceId/sessions',
    loadComponent: () =>
      import('./pages/workspaces/workspace-sessions/workspace-sessions.component').then(
        (m) => m.WorkspaceSessionsComponent,
      ),
    canActivate: [authGuard, formLockGuard],
  },
  {
    path: 'workspaces/:workspaceId/sessions/:sessionId/forms',
    loadComponent: () =>
      import('./pages/workspaces/workspace-forms/workspace-forms.component').then(
        (m) => m.WorkspaceFormsComponent,
      ),
    canActivate: [authGuard, formLockGuard],
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];
